import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import {
  useLoginMutation,
  useLoginTotpMutation,
  useLoginEmailCodeMutation,
  useResendLoginCodeMutation,
  useSetupTotpEnforcedMutation,
  useEnableTotpEnforcedMutation,
  type TotpSetupResponse,
} from '@shared/api';
import { useAppDispatch, useAppSelector } from '@app/hooks';
import { setTokens } from '@features/auth/model/slice';
import { announcementsReset } from '@features/announcements/model/slice';
import { osnovaApi } from '@shared/api';
import { Button } from '@shared/ui/Button';
import { Input, Label, Field } from '@shared/ui/Input';
import { ThemeToggle } from '@widgets/AppShell/ThemeToggle';
import { Brand } from '@shared/ui/Brand';
import { riseIn } from '@shared/theme/animations';

const driftA = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  50%      { transform: translate(40px, -30px) scale(1.05); }
`;

const driftB = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  50%      { transform: translate(-30px, 40px) scale(0.95); }
`;

const Page = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.background};
  position: relative;
  overflow: hidden;
`;

const BlobA = styled.div`
  position: absolute;
  top: -120px;
  left: -120px;
  width: 360px;
  height: 360px;
  border-radius: 50%;
  background: radial-gradient(closest-side, ${({ theme }) => theme.colors.primary}33, transparent);
  filter: blur(40px);
  animation: ${driftA} 18s ease-in-out infinite;
  pointer-events: none;
`;

const BlobB = styled.div`
  position: absolute;
  bottom: -160px;
  right: -100px;
  width: 420px;
  height: 420px;
  border-radius: 50%;
  background: radial-gradient(closest-side, ${({ theme }) => theme.colors.primary}22, transparent);
  filter: blur(50px);
  animation: ${driftB} 22s ease-in-out infinite;
  pointer-events: none;
`;

const TopRight = styled.div`
  position: absolute;
  top: ${({ theme }) => theme.spacing(5)};
  right: ${({ theme }) => theme.spacing(5)};
  z-index: 2;
`;

const Panel = styled.div`
  position: relative;
  width: 420px;
  padding: ${({ theme }) => theme.spacing(9)};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.xl};
  box-shadow: ${({ theme }) => theme.shadow.lg};
  z-index: 1;
  animation: ${riseIn} 520ms ${({ theme }) => theme.motion.easeOut};
`;

const BrandRow = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const Subtitle = styled.div`
  text-align: center;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-bottom: ${({ theme }) => theme.spacing(7)};
  font-size: 13px;
  letter-spacing: 0.02em;
`;

const ErrorBox = styled.div`
  color: ${({ theme }) => theme.colors.status.danger};
  background: ${({ theme }) => theme.colors.status.danger}15;
  border: 1px solid ${({ theme }) => theme.colors.status.danger}33;
  padding: ${({ theme }) => theme.spacing(2.5)} ${({ theme }) => theme.spacing(3.5)};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 13px;
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  animation: ${riseIn} 280ms ${({ theme }) => theme.motion.easeOut};
`;

const Hint = styled.div`
  text-align: center;
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: 12.5px;
  line-height: 1.5;
  margin-bottom: ${({ theme }) => theme.spacing(5)};
`;

const QrFrame = styled.div`
  display: flex;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing(3)};
  background: #fff;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  margin: ${({ theme }) => theme.spacing(4)} 0 ${({ theme }) => theme.spacing(3)};

  img {
    width: 200px;
    height: 200px;
    image-rendering: pixelated;
  }
`;

const QrPlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 224px;
  margin: ${({ theme }) => theme.spacing(4)} 0 ${({ theme }) => theme.spacing(3)};
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: 13px;
`;

const SecretLabel = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-bottom: ${({ theme }) => theme.spacing(1.5)};
`;

const Secret = styled.code`
  display: block;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(3)};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-family: 'JetBrains Mono', 'SF Mono', Consolas, monospace;
  font-size: 12.5px;
  word-break: break-all;
  user-select: all;
  margin-bottom: ${({ theme }) => theme.spacing(5)};
`;

const BackLink = styled.button`
  background: transparent;
  border: 0;
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: 12px;
  padding: ${({ theme }) => theme.spacing(2)} 0;
  cursor: pointer;
  width: 100%;
  margin-top: ${({ theme }) => theme.spacing(3)};
  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const Footer = styled.div`
  margin-top: ${({ theme }) => theme.spacing(6)};
  text-align: center;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

// Обёртка поля пароля: даёт место справа под кнопку-«глазик», которая
// переключает видимость пароля. Кнопка позиционируется поверх правого края
// инпута, поэтому у самого инпута — увеличенный правый padding.
const PasswordWrap = styled.div`
  position: relative;
`;

const RevealButton = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  height: 40px;
  width: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 0;
  padding: 0;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text.muted};
  transition: color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
  &:focus-visible {
    outline: none;
    color: ${({ theme }) => theme.colors.primary};
  }
`;

// Иконки «показать/скрыть пароль» — inline SVG, без внешних зависимостей.
function EyeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.7" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M3 3l18 18M10.6 10.7a3 3 0 0 0 4.2 4.2M9.9 5.2A9.6 9.6 0 0 1 12 5c6.5 0 10 7 10 7a17.6 17.6 0 0 1-3.6 4.4M6.2 6.2A17.7 17.7 0 0 0 2 12s3.5 7 10 7a9.5 9.5 0 0 0 3.4-.6"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Некоторые 401/403 бэкенд отдаёт с message = внутренний код ("unauthorized"/
// "forbidden") — это не текст для пользователя. Такие прячем за понятным
// fallback'ом; осмысленные серверные сообщения (напр. «Неверный email или
// пароль») показываем как есть.
const OPAQUE_ERROR_CODES = new Set(['unauthorized', 'forbidden']);
function pickErrorMessage(err: unknown, fallback: string): string {
  const msg = (err as { data?: { error?: { message?: string } } })?.data?.error
    ?.message;
  if (!msg || OPAQUE_ERROR_CODES.has(msg.trim().toLowerCase())) return fallback;
  return msg;
}

// 'mfa_setup' — обязательная 2FA включена на сервере, а у юзера её нет:
// после пароля показываем QR Яндекс Ключа и просим код, вместо того чтобы
// пустить внутрь. Токены выдаются только после подтверждения кода.
type Step = 'password' | 'totp' | 'email_code' | 'mfa_setup';

export function LoginPage() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isAuthed = useAppSelector((s) => !!s.auth.accessToken);
  const [login, { isLoading: isLoggingIn }] = useLoginMutation();
  const [loginTotp, { isLoading: isVerifying }] = useLoginTotpMutation();
  const [loginEmailCode, { isLoading: isCheckingCode }] = useLoginEmailCodeMutation();
  const [resendLoginCode, { isLoading: isResending }] = useResendLoginCodeMutation();
  const [setupTotpEnforced, { isLoading: isPreparingQr }] =
    useSetupTotpEnforcedMutation();
  const [enableTotpEnforced, { isLoading: isEnrolling }] =
    useEnableTotpEnforcedMutation();
  const [step, setStep] = useState<Step>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [challenge, setChallenge] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Enforcement-флоу: короткий setup-токен + данные QR (secret/otpauth/png).
  const [setupToken, setSetupToken] = useState<string | null>(null);
  const [setupData, setSetupData] = useState<TotpSetupResponse | null>(null);
  const [enrollCode, setEnrollCode] = useState('');
  // Вход с кодом из письма: адрес под маской и подтверждение повторной отправки.
  const [emailHint, setEmailHint] = useState<string | null>(null);
  const [resent, setResent] = useState(false);

  // Экран входа — граница сессии: после выхода, истечения сессии или кика 2FA
  // в памяти вкладки не должно остаться данных прежнего пользователя (кэш API
  // держится 5 минут и не перезапрашивается при монтировании — иначе новый
  // пользователь увидел бы чужие заявки, тексты, права из /info). Сбрасываем
  // здесь, а не в момент clearTokens: к этому моменту защищённые экраны уже
  // размонтированы и не перезапросят данные без токена.
  useEffect(() => {
    if (isAuthed) return;
    dispatch(osnovaApi.util.resetApiState());
    dispatch(announcementsReset());
  }, [isAuthed, dispatch]);

  // baseQuery редиректит сюда с ?expired=1 когда refresh-токен невалиден —
  // показываем явное сообщение, чтобы юрист не подумал что просто «забыл
  // войти». После успешного логина URL станет '/' — сообщение пропадёт.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('expired') === '1') {
      setError('Сессия истекла, войдите снова.');
    }
  }, []);

  if (isAuthed) {
    return <Navigate to="/" replace />;
  }

  function applyTokens(res: {
    access_token: string;
    refresh_token: string;
  }) {
    // Вход — вторая граница сессии: весь кэш API (UserContext, доступы к
    // агентам, данные модулей) сбрасывается до того, как появится токен, —
    // ни один экран не покажет кэш предыдущего пользователя.
    dispatch(osnovaApi.util.resetApiState());
    dispatch(announcementsReset());
    dispatch(
      setTokens({
        accessToken: res.access_token,
        refreshToken: res.refresh_token,
      }),
    );
    navigate('/', { replace: true });
  }

  async function onSubmitPassword(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const res = await login({ email, password }).unwrap();
      if (res.kind === 'authenticated') {
        applyTokens(res);
        return;
      }
      if (res.kind === 'mfa_setup_required') {
        // Обязательная 2FA, но её нет — переходим к настройке Яндекс Ключа
        // и сразу тянем QR по короткому setup-токену.
        setSetupToken(res.setup_token);
        setEnrollCode('');
        setStep('mfa_setup');
        await fetchEnrollQr(res.setup_token);
        return;
      }
      if (res.kind === 'email_code_required') {
        // Пароль верный, вход подтверждает код из письма на служебную почту.
        setChallenge(res.challenge);
        setEmailHint(res.email_hint);
        setResent(false);
        setCode('');
        setStep('email_code');
        return;
      }
      // TOTP required → переключаемся на ввод кода
      setChallenge(res.challenge);
      setCode('');
      setStep('totp');
    } catch (err) {
      setError(pickErrorMessage(err, 'Не удалось войти'));
    }
  }

  // Тянет pending secret + QR по setup-токену. Вынесено, чтобы дёргать и при
  // входе в шаг, и кнопкой «повторить» если первый запрос упал.
  async function fetchEnrollQr(token: string) {
    setError(null);
    try {
      const data = await setupTotpEnforced({ setupToken: token }).unwrap();
      setSetupData(data);
    } catch (err) {
      setError(
        pickErrorMessage(
          err,
          'Не удалось подготовить QR-код. Попробуйте войти заново.',
        ),
      );
    }
  }

  async function onSubmitEnroll(e: FormEvent) {
    e.preventDefault();
    if (!setupToken) return;
    setError(null);
    try {
      const res = await enableTotpEnforced({
        setupToken,
        code: enrollCode,
      }).unwrap();
      // Бэкенд активировал 2FA и вернул полноценный TokenPair — входим.
      applyTokens(res);
    } catch (err) {
      setError(pickErrorMessage(err, 'Неверный код'));
    }
  }

  async function onSubmitTotp(e: FormEvent) {
    e.preventDefault();
    if (!challenge) return;
    setError(null);
    try {
      const res = await loginTotp({ challenge, code }).unwrap();
      applyTokens(res);
    } catch (err) {
      setError(pickErrorMessage(err, 'Неверный код'));
    }
  }

  async function onSubmitEmailCode(e: FormEvent) {
    e.preventDefault();
    if (!challenge) return;
    setError(null);
    try {
      const res = await loginEmailCode({ challenge, code }).unwrap();
      applyTokens(res);
    } catch (err) {
      setError(pickErrorMessage(err, 'Неверный код'));
    }
  }

  async function onResendCode() {
    if (!challenge) return;
    setError(null);
    try {
      const res = await resendLoginCode({ challenge }).unwrap();
      setEmailHint(res.email_hint);
      setResent(true);
      setCode('');
    } catch (err) {
      setError(pickErrorMessage(err, 'Не удалось отправить код'));
    }
  }

  function backToPassword() {
    setStep('password');
    setChallenge(null);
    setCode('');
    setSetupToken(null);
    setSetupData(null);
    setEnrollCode('');
    setEmailHint(null);
    setResent(false);
    setError(null);
  }

  return (
    <Page>
      <BlobA aria-hidden />
      <BlobB aria-hidden />
      <TopRight>
        <ThemeToggle />
      </TopRight>
      <Panel>
        <BrandRow>
          <Brand $size="xl" $animate />
        </BrandRow>
        <Subtitle>
          {step === 'password'
            ? 'Внутренняя платформа САТКОМ'
            : step === 'mfa_setup'
              ? 'Настройка двухфакторной аутентификации'
              : step === 'email_code'
                ? 'Подтверждение входа'
                : 'Двухфакторная аутентификация'}
        </Subtitle>
        {error && <ErrorBox>{error}</ErrorBox>}

        {step === 'password' ? (
          <form onSubmit={onSubmitPassword}>
            <Field>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="user@satcom.local"
                required
              />
            </Field>
            <Field>
              <Label htmlFor="password">Пароль</Label>
              <PasswordWrap>
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  style={{ paddingRight: 44 }}
                />
                <RevealButton
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                  aria-pressed={showPassword}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </RevealButton>
              </PasswordWrap>
            </Field>
            <Button type="submit" $size="lg" $block disabled={isLoggingIn}>
              {isLoggingIn ? 'Вход…' : 'Войти'}
            </Button>
          </form>
        ) : step === 'email_code' ? (
          <form onSubmit={onSubmitEmailCode}>
            <Hint>
              Код отправлен на {emailHint ?? 'вашу служебную почту'}. Введите его
              — код действует 10 минут.
              {resent && (
                <>
                  <br />
                  Письмо отправлено ещё раз.
                </>
              )}
            </Hint>
            <Field>
              <Label htmlFor="email-code">Код из письма</Label>
              <Input
                id="email-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="123456"
                maxLength={6}
                pattern="\d{6}"
                required
                autoFocus
                style={{ letterSpacing: '0.6em', textAlign: 'center', fontSize: 22 }}
              />
            </Field>
            <Button type="submit" $size="lg" $block disabled={isCheckingCode || code.length !== 6}>
              {isCheckingCode ? 'Проверка…' : 'Войти'}
            </Button>
            <BackLink type="button" onClick={onResendCode} disabled={isResending}>
              {isResending ? 'Отправляем…' : 'Отправить код ещё раз'}
            </BackLink>
            <BackLink type="button" onClick={backToPassword}>
              ← Назад к вводу пароля
            </BackLink>
          </form>
        ) : step === 'totp' ? (
          <form onSubmit={onSubmitTotp}>
            <Hint>
              Введите 6-значный код из приложения-аутентификатора
              <br />
              (Яндекс Ключ, Google Authenticator).
            </Hint>
            <Field>
              <Label htmlFor="code">Код</Label>
              <Input
                id="code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) =>
                  setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                }
                placeholder="123456"
                maxLength={6}
                pattern="\d{6}"
                required
                autoFocus
                style={{ letterSpacing: '0.6em', textAlign: 'center', fontSize: 22 }}
              />
            </Field>
            <Button
              type="submit"
              $size="lg"
              $block
              disabled={isVerifying || code.length !== 6}
            >
              {isVerifying ? 'Проверка…' : 'Подтвердить'}
            </Button>
            <BackLink type="button" onClick={backToPassword}>
              ← Назад к вводу пароля
            </BackLink>
          </form>
        ) : (
          <form onSubmit={onSubmitEnroll}>
            <Hint>
              Для входа обязательна двухфакторная аутентификация. Откройте
              <b> Яндекс Ключ</b> (или другое приложение-аутентификатор),
              отсканируйте QR-код и введите 6-значный код.
            </Hint>
            {isPreparingQr ? (
              <QrPlaceholder>Готовим QR-код…</QrPlaceholder>
            ) : setupData ? (
              <>
                <QrFrame>
                  <img
                    src={`data:image/png;base64,${setupData.qr_png_base64}`}
                    alt="QR-код для добавления аккаунта в приложение-аутентификатор"
                  />
                </QrFrame>
                <SecretLabel>Если QR не сканируется — введите ключ вручную:</SecretLabel>
                <Secret>{setupData.secret}</Secret>
                <Field>
                  <Label htmlFor="enroll-code">Код из приложения</Label>
                  <Input
                    id="enroll-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={enrollCode}
                    onChange={(e) =>
                      setEnrollCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                    }
                    placeholder="123456"
                    maxLength={6}
                    pattern="\d{6}"
                    required
                    autoFocus
                    style={{ letterSpacing: '0.6em', textAlign: 'center', fontSize: 22 }}
                  />
                </Field>
                <Button
                  type="submit"
                  $size="lg"
                  $block
                  disabled={isEnrolling || enrollCode.length !== 6}
                >
                  {isEnrolling ? 'Проверка…' : 'Подтвердить и войти'}
                </Button>
              </>
            ) : (
              // QR не загрузился (setup-токен истёк / сеть) — даём повтор.
              <Button
                type="button"
                $size="lg"
                $block
                onClick={() => setupToken && fetchEnrollQr(setupToken)}
              >
                Повторить загрузку QR-кода
              </Button>
            )}
            <BackLink type="button" onClick={backToPassword}>
              ← Назад к вводу пароля
            </BackLink>
          </form>
        )}
        <Footer>ОСНОВА · САТКОМ</Footer>
      </Panel>
    </Page>
  );
}
